import moment from "moment";
import "moment-timezone";

import type { SupportedLocale } from "../../config/locales";
import type IntlProviderService from "../services/intl-provider";

import { supported_locales } from "../../config/locales";

export const SHORT_DATE_FORMAT = "YYYY-MM-DD";

// Input format YYYY-MM-DD
// Output format MM/DD/YYYY
export const formatDateSlashed = (date: string): string => {
  const raw_date = new Date(`${date}T00:00:00`); // otherwise 2021-05-12, returns 2021 May 11th at 8pm EST (ETC-4)
  const year = raw_date.getFullYear();
  let month = (1 + raw_date.getMonth()).toString();
  if (month.length < 2) {
    month = `0${month}`;
  }

  let day = raw_date.getDate().toString();
  if (day.length < 2) {
    day = `0${day}`;
  }

  return `${month}/${day}/${year}`;
};

/*
 * Checks if date_m is in the past (accounts date only)
 * uses moment() as value compared against
 *
 * uses .hours(0).minutes(0).seconds(0).milliseconds(0) instead of startOf('day')
 * because of bug https://github.com/moment/moment/issues/4152
 */
export function isDatePastFrom(
  date_m: moment.Moment,
  date_before_m: moment.Moment
): boolean {
  const against = date_before_m.clone().hours(0).minutes(0).seconds(0);

  return date_m.isBefore(against);
}

export function isDatePast(date: Date | string): boolean {
  return moment(date).isBefore(moment());
}

export function isToday(date: Date | string): boolean {
  return moment(date).isSame(moment(), "day");
}

export function isTomorrow(date: Date | string): boolean {
  return moment(date).isSame(moment().add(1, "day"), "day");
}

export function isDateFuture(date: Date | string): boolean {
  return moment(date).isAfter(moment());
}

export function getYear(timezone: string, today: Date) {
  return moment(today).tz(timezone).year();
}

export function getStartOfToday(timezone: string, today: Date) {
  return moment(today).tz(timezone).startOf("day");
}

export function getMomentFromRelativeNumberOfDay(
  relative_number_of_day: number,
  timezone: string,
  today: Date
) {
  const start_of_today = getStartOfToday(timezone, today);
  return start_of_today.add(relative_number_of_day, "days");
}

export function getDateFromRelativeNumberOfDay(
  relative_number_of_day: number,
  timezone: string,
  today: Date
): string {
  return getMomentFromRelativeNumberOfDay(
    relative_number_of_day,
    timezone,
    today
  ).format(SHORT_DATE_FORMAT);
}

export function formatRelativeNumberOfDayToLocaleDayMonth(
  intl_service: IntlProviderService,
  relative_number_of_day: number,
  busbud_locale: SupportedLocale,
  timezone: string,
  today: Date
): string {
  const locale = supported_locales[busbud_locale].full_locale;

  return intl_service
    .getDateTimeFormat(locale, {
      month: "2-digit",
      day: "2-digit",
      timeZone: timezone
    })
    .format(
      getMomentFromRelativeNumberOfDay(
        relative_number_of_day,
        timezone,
        today
      ).toDate()
    );
}

export function formatRelativeNumberOfDayToLocaleDayMonthLong(
  intl_service: IntlProviderService,
  relative_number_of_day: number,
  busbud_locale: SupportedLocale,
  timezone: string,
  today: Date
): string {
  const locale = supported_locales[busbud_locale].full_locale;

  return intl_service
    .getDateTimeFormat(locale, { dateStyle: "medium", timeZone: timezone })
    .format(
      getMomentFromRelativeNumberOfDay(
        relative_number_of_day,
        timezone,
        today
      ).toDate()
    );
}

export function formatDate(
  date: Date | string,
  format = SHORT_DATE_FORMAT
): string {
  return moment(date).format(format);
}

export function minutesToHours(minutes: number, today: Date): string {
  return moment(today).startOf("day").minute(minutes).format("h[h]m[m]");
}

export function getDateAfterFromToday(
  days: number,
  timezone: string,
  today: Date
): Date {
  return moment(today).tz(timezone).add(days, "days").toDate();
}

export function getNumberOfDayInYear(
  timezone: string,
  date: Date | string
): number {
  return moment(date).tz(timezone).dayOfYear();
}

export function isBefore(
  date: Date | string,
  date_before: Date | string
): boolean {
  return moment(date).isBefore(date_before);
}

export function isSameDate(
  date_1: Date | string,
  date_2: Date | string
): boolean {
  return moment(date_2).isSame(date_1, "date");
}

export function hoursToMidnight(timeZone: string): number {
  const today_in_tz = moment.tz(timeZone);
  const currentHour = today_in_tz.get("hours");
  const hoursRemaining = 24 - currentHour;
  return hoursRemaining;
}
